/**
 * Example detection guidance an AP clerk would write. Exceptions stay
 * user-authored — these are sentences for the exercise, not system types.
 * Matched by enum option name (trimmed, case-insensitive).
 */
export const EXAMPLE_DETECTION_GUIDANCE: Readonly<Record<string, string>> = {
  'invoice does not match the po/file.':
    'Catch-all. Raise whenever Matching says unmatchedLineCount is greater than 0 (any matchable line is UNMATCHED). Always raise this when lines are unmatched, even if another type also applies. Do not raise when every matchable line is matched.',
  'duplicate invoice number is detected.':
    'Raise only when Duplicate candidates lists at least one other invoice. Cite the sibling id, status, and total. Do not raise when that section says none.',
  'expected credit is missing.':
    'Raise when Matching says Expected cost record missing upstream: yes. That flag is the missing expected credit / cost record. Do not raise when the flag is no.',
  'freight rate differs from the established rate.':
    'Raise only when a Freight (or similar) line exists AND the facts give two different rates you can cite (invoice unit/total vs a supporting-doc rate or a matched CHARGE amount). If you only see one freight number, do not raise — do not invent an established rate.',
  'required documentation is missing.':
    'Raise when there is no Supporting documents (OCR) section, or it is missing what this invoice needs: product invoices need a receiving stamp or POD; freight invoices need a BOL or carrier confirmation. Cite what is absent. Do not raise if a relevant supporting-doc block is present.',
  'required charges are missing or incorrect.':
    'Raise when a product invoice has no line matched to CHARGE and no freight/pallet/unloading line, or a charge-like line is UNMATCHED. Do not raise on a freight-only invoice whose freight line is already matched to CHARGE.',
  'payment is on hold.':
    'Raise only when Payment hold says ON HOLD. Cite the reason if present. Do not raise when it says not on hold.',
  'payable is in famous, but vendor has not provided an invoice (missing invoice)':
    'Do not raise. This evaluator only runs on an invoice that already exists. A Famous payable with no vendor invoice cannot be detected from these facts.',
  'required approval is missing.':
    'Do not raise from Status: DRAFT or MATCHED alone — those are processing states, not a missing approval. Raise only if the facts explicitly say an approval is required and missing.',
};

export function exampleGuidanceForTypeName(name: string): string | null {
  const key = name.trim().toLowerCase();
  return EXAMPLE_DETECTION_GUIDANCE[key] ?? null;
}

export function applyExampleGuidance<
  T extends { exceptionTypeOptionId: string; detectionGuidance: string },
>(rules: readonly T[], typeNameById: ReadonlyMap<string, string>): T[] {
  return rules.map((rule) => {
    const name = typeNameById.get(rule.exceptionTypeOptionId);
    const example = name ? exampleGuidanceForTypeName(name) : null;
    if (!example) return rule;
    return { ...rule, detectionGuidance: example };
  });
}
